/**
 * Service Worker Registration and Communication
 * 
 * Handles registration of the media cache service worker and provides
 * an API for communicating with it from the main thread.
 */

const SW_PATH = '/media-cache-sw.js';

interface ServiceWorkerMessage {
    type: string;
    data?: any;
}

class ServiceWorkerManager {
    private registration: ServiceWorkerRegistration | null = null;
    private isSupported: boolean;

    constructor() {
        this.isSupported = 'serviceWorker' in navigator;
    }

    /**
     * Register the service worker
     */
    async register(): Promise<boolean> {
        if (!this.isSupported) {
            console.warn('[ServiceWorkerManager] Service workers not supported');
            return false;
        }

        try {
            this.registration = await navigator.serviceWorker.register(SW_PATH, {
                scope: '/',
            });

            console.log('[ServiceWorkerManager] Registered:', this.registration.scope);

            // Handle updates
            this.registration.addEventListener('updatefound', () => {
                const newWorker = this.registration?.installing;
                if (newWorker) {
                    newWorker.addEventListener('statechange', () => {
                        if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                            console.log('[ServiceWorkerManager] New version available');
                            // Optionally notify user about update
                        }
                    });
                }
            });

            return true;
        } catch (error) {
            console.error('[ServiceWorkerManager] Registration failed:', error);
            return false;
        }
    }

    /**
     * Unregister the service worker
     */
    async unregister(): Promise<boolean> {
        if (!this.registration) {
            return false;
        }

        try {
            const success = await this.registration.unregister();
            console.log('[ServiceWorkerManager] Unregistered:', success);
            this.registration = null;
            return success;
        } catch (error) {
            console.error('[ServiceWorkerManager] Unregister failed:', error);
            return false;
        }
    }

    /**
     * Send message to service worker
     */
    private sendMessage(message: ServiceWorkerMessage): Promise<any> {
        return new Promise((resolve, reject) => {
            if (!navigator.serviceWorker.controller) {
                reject(new Error('No active service worker'));
                return;
            }

            const messageChannel = new MessageChannel();
            
            messageChannel.port1.onmessage = (event) => {
                resolve(event.data);
            };

            navigator.serviceWorker.controller.postMessage(message, [messageChannel.port2]);

            // Timeout after 10 seconds
            setTimeout(() => {
                reject(new Error('Service worker message timeout'));
            }, 10000);
        });
    }

    /**
     * Clear the service worker cache
     */
    async clearCache(): Promise<boolean> {
        try {
            const result = await this.sendMessage({ type: 'CLEAR_CACHE' });
            console.log('[ServiceWorkerManager] Cache cleared:', result);
            return result?.success ?? false;
        } catch (error) {
            console.error('[ServiceWorkerManager] Clear cache failed:', error);
            return false;
        }
    }

    /**
     * Get cache statistics from service worker
     */
    async getCacheStats(): Promise<{
        entries: number;
        totalSize: number;
        maxSize: number;
    } | null> {
        try {
            return await this.sendMessage({ type: 'GET_CACHE_STATS' });
        } catch (error) {
            console.error('[ServiceWorkerManager] Get stats failed:', error);
            return null;
        }
    }

    /**
     * Prefetch URLs into service worker cache
     */
    async prefetch(urls: string[]): Promise<boolean> {
        try {
            const result = await this.sendMessage({ type: 'PREFETCH', data: { urls } });
            return result?.success ?? false;
        } catch (error) {
            console.error('[ServiceWorkerManager] Prefetch failed:', error);
            return false;
        }
    }

    /**
     * Check if service worker is active
     */
    isActive(): boolean {
        return !!navigator.serviceWorker?.controller;
    }

    /**
     * Get service worker status
     */
    getStatus(): 'unsupported' | 'unregistered' | 'installing' | 'waiting' | 'active' {
        if (!this.isSupported) return 'unsupported';
        if (!this.registration) return 'unregistered';
        if (this.registration.installing) return 'installing';
        if (this.registration.waiting) return 'waiting';
        if (this.registration.active) return 'active';
        return 'unregistered';
    }
}

// Singleton instance
export const serviceWorkerManager = new ServiceWorkerManager();

// Export class for testing
export { ServiceWorkerManager };
